import { createSelector } from "@reduxjs/toolkit";
import { get } from "es-toolkit/compat";

import {
  assert,
  isMultilingualProperty,
  nodeDesignsByIdSelector,
  type NodeDesignsState,
} from "@skilitics/nautilus-core-designer-data";

import type { GetManifestByTypeId } from "../../types/index.ts";
import { activeContentLanguageCodeSelector, getManifestSelector } from "../contextualSelectors/index.ts";

interface CreateOptions {
  designsById: NodeDesignsState["designsById"];
  getManifest: GetManifestByTypeId;
}

/** For tests do not use directly */
export const getMultilingualOptInEnabledCreate =
  ({ getManifest, designsById }: CreateOptions) =>
  (id: number, property: string, { lang }: { lang: string }) => {
    const nodeEntry = designsById[id];
    assert(nodeEntry, `Node with id ${id} not found`);
    const { typeId, config } = nodeEntry;
    const { designer: designerManifest } = getManifest(typeId);
    // check if it is multilingual opt-in property
    const optInConfig = get(designerManifest, `multilingual.${property}.optIn`);
    if (!optInConfig) {
      throw new Error(`Property ${property} is not a multilingual opt-in property on node of type ${typeId}.`);
    }

    const propertyValue = get(config, property);
    if (!isMultilingualProperty(propertyValue)) {
      return false;
    }
    return lang in propertyValue.__ml;
  };

export type GetMultilingualOptInEnabled = ReturnType<typeof getMultilingualOptInEnabledCreate>;

const getMultilingualOptInEnabledForLangSelector = createSelector(
  [getManifestSelector, nodeDesignsByIdSelector],
  (getManifest, designsById) => getMultilingualOptInEnabledCreate({ getManifest, designsById }),
);

export const getMultilingualOptInEnabledSelector = createSelector(
  [getMultilingualOptInEnabledForLangSelector, activeContentLanguageCodeSelector],
  (getMultilingualOptInEnabledForLang, lang) => (id: number, property: string) =>
    getMultilingualOptInEnabledForLang(id, property, { lang }),
);
